import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Clock, Flame, Gavel, Share2, Sparkles, Trophy, X } from 'lucide-react';
import JurySeatProgress from './JurySeatProgress';
import FollowButton from './FollowButton';
import { getCanonicalRenderedVerdictAsset, getCanonicalTrialVerdictText } from '../utils/verdictAssets';

let latestTrialCardDeps = null;
let StableTrialCardComponent = null;

export default function createTrialCard(deps) {
  latestTrialCardDeps = deps;

  if (StableTrialCardComponent) return StableTrialCardComponent;

  const TrialCard = React.memo(({ trial, isNew }) => {
  const {
    votingInProgress,
    BETA_JURY_SIZE,
    calculateOdds,
    user,
    canSeeQuarantinedTrials,
    recentVotes,
    formatTime,
    finalizeInProgressTrials,
    finalizeErrorTrials,
    animatingVotes,
    handleVote,
    setFlippedVerdicts,
    flippedVerdicts,
    VERDICT_LOADING_LINES,
    getStableSpicyPunchline,
    getVerdictCopy,
    setVerdictData,
    setShowVerdict,
  } = latestTrialCardDeps || {};
  const isQuick = trial.type === 'quick';
  const statusLower = String(trial.status || '').toLowerCase();
  const verdictLocking = ['closed_pending_verdict', 'sudden_death_closed_pending'].includes(statusLower);
  const ended = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(statusLower);
  const interactionClosed = verdictLocking || ended;
  const isSuddenDeathOpen = statusLower === 'sudden_death_open';
  const disabled =
    votingInProgress ||
    interactionClosed ||
    (isQuick && !trial.suddenDeath && (trial.totalVotes >= (trial.jurySize || BETA_JURY_SIZE) || statusLower === 'ready')) ||
    trial.hasVoted;

  const isLive =
    !interactionClosed &&
    (statusLower === 'filling' || statusLower === 'ready' || statusLower === 'active' || statusLower === 'sudden_death_open') &&
    (trial.timeLeft == null || trial.timeLeft > 0);


  const oddsA = calculateOdds(trial, 'A');
  const oddsB = calculateOdds(trial, 'B');

  const currentUserId = user?.id;
  const creatorId = trial.created_by || trial.creator_id; // ✅ created_by matches your app user.id
  const creatorUsername = trial.creator_username || `Judge${creatorId?.slice(0, 6)}`;
  const isSeededStarterTrial = Boolean(
    trial.is_seeded
    || trial.seed_source
    || trial?.verdict_data?.seeded
  );
  const isTrialCreator = !!(currentUserId && creatorId && currentUserId === creatorId);

  const creatorBlocked = isTrialCreator;
  const underReviewForUser = Boolean(trial.is_quarantined && !canSeeQuarantinedTrials);
  const voteDisabled = disabled || creatorBlocked || underReviewForUser;
  const rawVotesA = Number(trial.votesA || 0);
  const rawVotesB = Number(trial.votesB || 0);
  const rawTotalVotes = Number(trial.totalVotes || 0);
  const recentVoteEntry = recentVotes?.[trial.id] || null;
  const normalizedRecentSide = (() => {
    const side = String(
      recentVoteEntry?.side || trial.userVoteSide || trial.userVote || ''
    ).toUpperCase();
    return side === 'A' || side === 'B' ? side : null;
  })();
  const optimisticVotesA = trial.hasVoted && normalizedRecentSide === 'A' ? 1 : 0;
  const optimisticVotesB = trial.hasVoted && normalizedRecentSide === 'B' ? 1 : 0;
  const displayedVotesA = Math.max(rawVotesA, optimisticVotesA);
  const displayedVotesB = Math.max(rawVotesB, optimisticVotesB);
  const displayedTotalVotes = Math.max(
    rawTotalVotes,
    displayedVotesA + displayedVotesB,
    trial.hasVoted ? 1 : 0
  );
  const displayedJuryFilled = Math.min(
    Number(trial.jurySize || BETA_JURY_SIZE),
    Math.max(rawTotalVotes, displayedVotesA + displayedVotesB, trial.hasVoted ? 1 : 0)
  );
  const countdownStageRef = useRef(null);
  const voteDebugRef = useRef('');

  useEffect(() => {
    if (interactionClosed || trial.timeLeft == null) return;
    const stage = trial.timeLeft <= 0 ? 'expired' : trial.timeLeft <= 10 ? 'critical' : trial.timeLeft <= 60 ? 'warning' : 'running';
    if (countdownStageRef.current === stage) return;
    countdownStageRef.current = stage;
    console.log('[trial_countdown_render]', {
      trial_id: trial.id,
      status: trial.status,
      time_left: trial.timeLeft,
      stage,
    });
  }, [interactionClosed, trial.id, trial.status, trial.timeLeft]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!trial.hasVoted) return;
    const key = `${trial.id}|${normalizedRecentSide || 'none'}|${displayedVotesA}|${displayedVotesB}`;
    if (voteDebugRef.current === key) return;
    voteDebugRef.current = key;
    console.log('[trial_card_vote_state]', {
      trial_id: trial.id,
      normalizedRecentSide,
      displayedVotesA,
      displayedVotesB,
      rawVotesA,
      rawVotesB,
      hasVoted: trial.hasVoted,
    });
  }, [trial.id, trial.hasVoted, normalizedRecentSide, displayedVotesA, displayedVotesB, rawVotesA, rawVotesB]);

  return (
    <div
      className={`relative rounded-2xl p-4 md:p-5 border-2 bg-slate-800 ${isNew ? 'animate-slide-in border-emerald-500/50' : 'border-slate-700'}`}
    >
      {/* Corner badges */}
      <div className="absolute top-3 right-3 flex items-center gap-2 text-xs font-black z-10">
        {underReviewForUser && (
          <span className="bg-amber-600 text-white px-2 py-1 rounded">UNDER REVIEW</span>
        )}
        {isSuddenDeathOpen && (
          <span className="bg-red-500 text-white px-2 py-1 rounded">SUDDEN DEATH</span>
        )}
      </div>

      {isSuddenDeathOpen && (
        <div className="mb-3 rounded-xl border border-amber-400/60 bg-amber-500/10 p-3">
          <div className="text-sm font-black text-amber-300">⚡ Sudden Death</div>
          <div className="text-xs text-amber-100/90">
            Tie detected. Overtime ends in {formatTime(Math.max(0, trial.timeLeft || 0)) || 'soon'}.
          </div>
        </div>
      )}

      {/* Title row - NOW CLICKABLE */}
      <div className="flex items-start gap-3">
        <div className="text-4xl leading-none">{trial.image}</div>
        <div className="flex-1">
          {underReviewForUser ? (
            <Link to={`/t/${trial.id}`} className="hover:text-amber-300 transition-colors">
              <h3 className="text-xl font-black text-slate-50 hover:underline">{trial.title}</h3>
            </Link>
          ) : (
            <Link
              to={`/t/${trial.id}`}
              className="hover:text-purple-400 transition-colors"
            >
              <h3 className="text-xl font-black text-slate-50 hover:underline">{trial.title}</h3>
            </Link>
          )}
          <div className="text-xs text-slate-400 flex items-center gap-3 mt-1">
            {trial.timeLeft != null && trial.timeLeft > 0 && !ended && (
              <span className={`inline-flex items-center gap-1 font-mono font-bold ${
                trial.timeLeft <= 10 ? 'text-red-400' : 
                trial.timeLeft < 60 ? 'text-yellow-400' : 'text-slate-400'
              }`}>
                <Clock size={14} /> {formatTime(Math.max(0, trial.timeLeft))}
              </span>
            )}
            {window.SHOW_JURY_PROGRESS_UI !== false ? (
              <JurySeatProgress
                filled={displayedJuryFilled}
                total={trial.jurySize || BETA_JURY_SIZE}
                compact={true}
                showLabel={false}
              />
            ) : null}
            {!ended && (
              <span className="inline-flex items-center gap-1">
                <Gavel size={14} /> {displayedTotalVotes}/{trial.jurySize || BETA_JURY_SIZE} jurors
              </span>
            )}
          </div>
          {!interactionClosed && finalizeInProgressTrials.has(trial.id) && (
            <div className="mt-1 text-[11px] text-slate-300">
              Finalizing trial...
            </div>
          )}
          {!interactionClosed && !finalizeInProgressTrials.has(trial.id) && finalizeErrorTrials.has(trial.id) && null}
        </div>
      </div>

      {/* Creator line for normal user-created trials; seeded prompts stay system-neutral */}
      {isSeededStarterTrial ? (
        <div className="mt-3 flex items-center justify-between py-2 px-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Fresh Prompt</div>
            <div className="text-xs text-slate-300 font-medium">Starter trial</div>
          </div>
        </div>
      ) : creatorId && (
        <div className="mt-3 flex items-center justify-between py-2 px-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-xs font-bold">
              {creatorUsername[0]?.toUpperCase()}
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Created by</div>
              {trial.creator_username ? (
                <Link
                  to={`/user/${trial.creator_username}`}
                  className="text-xs text-slate-300 font-medium hover:text-purple-300 hover:underline"
                >
                  @{creatorUsername}
                </Link>
              ) : (
                <div className="text-xs text-slate-300 font-medium">@{creatorUsername}</div>
              )}
            </div>
          </div>
          <FollowButton
            targetUserId={creatorId}
            targetUsername={creatorUsername}
          />
        </div>
      )}

      {/* Live Activity Badge */}
      {recentVotes[trial.id] && recentVotes[trial.id].count > 0 && recentVotes[trial.id].source === 'realtime' && !trial.hasVoted && (
        <div className="mt-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-pulse inline-block">
          <Activity size={14} />
          <span>{recentVotes[trial.id].count} vote{recentVotes[trial.id].count !== 1 ? 's' : ''}/min</span>
        </div>
      )}

      {trial.homeCrewSignal?.line ? (
        <div className="mt-3 rounded-lg border border-cyan-400/20 bg-cyan-950/20 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-200/75">
            Your people
          </div>
          <div className="mt-1 text-xs font-medium text-cyan-50/90 line-clamp-2">
            {trial.homeCrewSignal.crewEmoji ? `${trial.homeCrewSignal.crewEmoji} ` : ''}{trial.homeCrewSignal.line}
          </div>
        </div>
      ) : null}

      {verdictLocking && (
        <div className="mt-4 rounded-xl border border-yellow-500/35 bg-yellow-900/20 p-4">
          <div className="text-sm font-bold text-yellow-200">Jury locked. Verdict landing now.</div>
          <div className="mt-1 text-xs text-yellow-100/80">
            No more votes. We&apos;re turning the jury result into the final ruling.
          </div>
        </div>
      )}

      {/* Post-verdict only: show side bars and totals */}
      {ended && (
        <>
          <div className="space-y-3 mt-4">
            {/* Side A Bar */}
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-purple-300">{trial.sideA}</span>
                <span className="text-sm font-bold text-purple-300">
                  {displayedVotesA} vote{displayedVotesA !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-500 ${
                    animatingVotes.has(trial.id) ? 'animate-pulse' : ''
                  }`}
                  style={{
                    width: `${displayedTotalVotes > 0 ? (displayedVotesA / displayedTotalVotes) * 100 : 0}%`
                  }}
                />
              </div>
            </div>

            {/* Side B Bar */}
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-pink-300">{trial.sideB}</span>
                <span className="text-sm font-bold text-pink-300">
                  {displayedVotesB} vote{displayedVotesB !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r from-pink-500 to-pink-400 rounded-full transition-all duration-500 ${
                    animatingVotes.has(trial.id) ? 'animate-pulse' : ''
                  }`}
                  style={{
                    width: `${displayedTotalVotes > 0 ? (displayedVotesB / displayedTotalVotes) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </div>

          {displayedTotalVotes > 0 && (
            <div className="flex items-center justify-between text-xs text-slate-400 mt-3 mb-2">
              <div className="flex items-center gap-1">
                <Flame size={14} className={recentVotes[trial.id]?.count > 5 ? 'text-orange-500' : ''} />
                <span>
                  {recentVotes[trial.id]?.count > 5 && '🔥 HOT! '}
                  {displayedTotalVotes} total vote{displayedTotalVotes !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">
{displayedTotalVotes}/{trial.jurySize || BETA_JURY_SIZE} jurors
                </span>
                {isLive && (
                  <span className="text-green-400 text-xs font-bold animate-pulse flex items-center gap-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    LIVE
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Odds chips */}
      <div className="mt-3 flex gap-2 text-xs">
        <span className="px-2 py-1 rounded-full bg-purple-900/30 border border-purple-500/40 text-purple-200 font-bold">
          {trial.sideA}: {oddsA}x
        </span>
        <span className="px-2 py-1 rounded-full bg-amber-900/30 border border-amber-500/40 text-amber-200 font-bold">
          {trial.sideB}: {oddsB}x
        </span>
      </div>

      {/* Actions */}
      {underReviewForUser && (
        <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-900/20 p-3 text-center">
          <div className="text-sm font-bold text-amber-200">Under Review</div>
          <div className="text-xs text-amber-100/80 mt-1">Voting is disabled while this trial is being reviewed.</div>
        </div>
      )}
      {verdictLocking ? (
        <div className="mt-4 rounded-xl border border-yellow-500/35 bg-yellow-900/20 p-4 text-center">
          <div className="font-bold text-yellow-200">Ruling in progress</div>
          <div className="mt-1 text-xs text-yellow-100/80">
            The jury is locked in. Open the trial again in a moment for the verdict.
          </div>
        </div>
      ) : trial.hasVoted && !ended ? (
        // Show "Already Voted" badge when user has voted
        <div className="mt-4 bg-gradient-to-r from-emerald-600/20 to-green-600/20 border-2 border-emerald-500/50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-emerald-400 font-bold">
            <Trophy size={20} />
            <span>You've voted on this trial!</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Watch the results unfold in real-time
          </div>
        </div>
      ) : (
        <div className="mt-4">
  {creatorBlocked && (
    <div className="mt-1 mb-2 text-xs text-slate-400 text-center">
      This is your call. You can’t cast a separate jury vote. Share it to get jurors.
    </div>
  )}

  <div className="grid grid-cols-2 gap-2">
    <button
      onClick={() => handleVote(trial.id, 'A')}
      disabled={voteDisabled}
      className={`py-3 rounded-xl font-bold transition-all border shadow-[0_12px_30px_-18px_rgba(0,0,0,0.9)] ${
        voteDisabled
          ? 'border-slate-700 bg-slate-800 text-slate-500 cursor-not-allowed'
          : 'border-purple-400/50 bg-gradient-to-r from-purple-700 via-fuchsia-700 to-purple-600 text-white hover:from-purple-600 hover:via-fuchsia-600 hover:to-purple-500'
      }`}
    >
      {trial.sideA}
    </button>

    <button
      onClick={() => handleVote(trial.id, 'B')}
      disabled={voteDisabled}
      className={`py-3 rounded-xl font-bold transition-all border shadow-[0_12px_30px_-18px_rgba(0,0,0,0.9)] ${
        voteDisabled
          ? 'border-slate-700 bg-slate-800 text-slate-500 cursor-not-allowed'
          : 'border-amber-300/50 bg-gradient-to-r from-amber-700 via-yellow-600 to-amber-500 text-slate-950 hover:from-amber-600 hover:via-yellow-500 hover:to-amber-400'
      }`}
    >
      {trial.sideB}
    </button>
  </div>
</div>
)}
       {/* Reveal Verdict Button for Completed Trials */}
      {ended && (
        <button
          onClick={() => {
            setFlippedVerdicts(prev => {
              const updated = new Set(prev);
              if (updated.has(trial.id)) {
                updated.delete(trial.id);
              } else {
                updated.add(trial.id);
              }
              return updated;
            });
          }}
          className="w-full mb-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
        >
          {flippedVerdicts.has(trial.id) ? (
            <>
              <X size={18} />
              Hide Verdict
            </>
          ) : (
            <>
              <Sparkles size={18} />
              🔄 Reveal Verdict
            </>
          )}
        </button>
      )}

      {/* Show flipped verdict */}
      {ended && flippedVerdicts.has(trial.id) && (() => {
        const vd = trial.verdict_data || {};
        const isTie = vd.winner === 'Tie' || (!vd.winner && trial.votesA === trial.votesB);
        const winnerLabel = isTie ? "IT'S A TIE!" : vd.winner === 'A' ? trial.sideA : vd.winner === 'B' ? trial.sideB : (trial.votesA > trial.votesB ? trial.sideA : trial.sideB);
        const pct = isTie ? 50 : vd.winnerPct ?? ((Math.max(trial.votesA, trial.votesB) / Math.max(trial.totalVotes,1)) * 100).toFixed(1);
        const canonicalVerdictText = getCanonicalTrialVerdictText({
          id: trial.id,
          title: trial.title,
          side_a: trial.sideA,
          side_b: trial.sideB,
          verdict_data: vd,
        });
        const line = isTie 
          ? "The jury is deadlocked. This one goes to sudden death... or not." 
          : (canonicalVerdictText.captionFull || canonicalVerdictText.captionShort || vd.punchline || getStableSpicyPunchline(trial, winnerLabel));
        
        // ✅ Get AI image if available
const aiImageUrl = getCanonicalRenderedVerdictAsset({ id: trial.id, verdict_data: vd }).url;
if (process.env.NODE_ENV !== 'production') {
  console.log('[trial_card_verdict_image]', {
    trial_id: trial.id,
    raw_share_card_url: vd.share_card_url ?? null,
    raw_background_image_url: vd.background_image_url ?? null,
    raw_aiImage: vd.aiImage ?? null,
    raw_verdict_image_url: vd.verdict_image_url ?? null,
    chosen_imageUrl: aiImageUrl,
  });
}

// ✅ "Meme is cooking" grace window after trial ends
const GRACE_MS = 15000;
const endedAt = trial.closedAt || trial.closed_at || trial.createdAt || Date.now();
const withinGrace = Date.now() - endedAt < GRACE_MS;

// Show loading if we don't have an image yet and we're within the grace window
const isEnded = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(trial.status);
const isLoadingAIImage = isEnded && !aiImageUrl && withinGrace;

        return (
          <div className="mb-4 relative rounded-xl overflow-hidden border-4 border-yellow-400">
            {isLoadingAIImage ? (
              // 🎨 LOADING STATE - Verdict meme is being generated
              <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 p-6 relative overflow-hidden">
                {/* Animated pulse overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-300/20 to-purple-500/0 animate-pulse" />

                <div className="text-center text-white relative z-10">
                  {/* Jury seats visual - shows all seats filled */}
                  <div className="mb-3">
                    <JurySeatProgress
                      filled={trial.totalVotes || trial.jurySize || BETA_JURY_SIZE}
                      total={trial.jurySize || BETA_JURY_SIZE}
                      showLabel={false}
                      compact={true}
                      className="justify-center"
                    />
                    <div className="text-xs text-yellow-300/80 font-semibold mt-1 animate-pulse">
                      ✅ Jury seated
                    </div>
                  </div>

                  <div className="text-lg font-black text-yellow-300 mb-2">
                    {VERDICT_LOADING_LINES[Math.floor(Date.now() / 2000) % VERDICT_LOADING_LINES.length]}
                  </div>
                  <div className="text-sm text-white/70 mb-4">Generating verdict image…</div>

                  {/* Preview of winner */}
                  <div className="bg-black/30 rounded-xl p-4 mb-3">
                    <div className="text-xs text-yellow-300/70 mb-1">{vd.verdictType || 'VERDICT'}</div>
                    <div className="text-2xl font-black text-yellow-300">{winnerLabel}</div>
                    <div className="text-sm text-white/80">{pct}% of the jury</div>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-ping"></div>
                    <span>~10 seconds</span>
                  </div>

                  {/* Future sponsor slot */}
                  {/* <div className="text-xs text-white/40 mt-4">This verdict brought to you by [Sponsor]</div> */}
                </div>
              </div>
            ) : aiImageUrl ? (
              // AI-Generated Background
              <>
                <img
                  src={aiImageUrl}
                  alt="Verdict background"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-center text-white">
                  <div className="text-sm font-black text-yellow-300 mb-2">{vd.verdictType || 'FINAL VERDICT'}</div>
                  <div className="text-4xl font-black text-yellow-300 mb-2">{winnerLabel}</div>
                  <div className="text-white font-bold mb-3">{pct}% of the jury</div>
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl italic text-sm">"{line}"</div>
                  <div className="mt-3 text-xs text-white/70">
                    Final: {trial.votesA} - {trial.votesB} • {trial.totalVotes} jurors {vd.verdictNumber ? <>• #{vd.verdictNumber}</> : null}
                  </div>
                </div>
              </>
            ) : (
              // Purple Card Fallback
              <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 p-6">
                <div className="text-center text-white">
                  <div className="text-sm font-black text-yellow-300 mb-2">{vd.verdictType || 'FINAL VERDICT'}</div>
                  <div className="text-4xl font-black text-yellow-300 mb-2">{winnerLabel}</div>
                  <div className="text-white font-bold mb-3">{pct}% of the jury</div>
                  <div className="bg-white/10 px-4 py-3 rounded-xl italic text-sm">"{line}"</div>
                  <div className="mt-2 text-xs text-white/70">Generating verdict image…</div>
                  <div className="mt-3 text-xs text-white/70">
                    Final: {trial.votesA} - {trial.votesB} • {trial.totalVotes} jurors {vd.verdictNumber ? <>• #{vd.verdictNumber}</> : null}
                  </div>
                </div>
              </div>
            )}
            
            {/* Share Button - Always shown */}
            <div className={aiImageUrl ? "absolute bottom-4 left-6 right-6 z-10" : "p-6 pt-0"}>
              <button
                onClick={() => {
                  const winnerSide = vd.winner || (trial.votesA === trial.votesB ? 'Tie' : trial.votesA > trial.votesB ? 'A' : 'B');
                  const fb = getVerdictCopy(trial, winnerSide);

                  setVerdictData({
                    trial,
                    trialId: trial.id,
                    verdict_data: vd,
                    winner: winnerSide,
                    winnerPct: pct,
                    punchline: canonicalVerdictText.captionFull || line,
                    verdictType: vd.verdictType || fb.verdictType || 'FINAL VERDICT',
                    verdictNumber: vd.verdictNumber,
                    templatePunchlines: vd.templatePunchlines || fb.punchlines,
                    aiPunchlines: vd.aiPunchlines || fb.aiPunchlines,
                    aiImage: aiImageUrl, // ✅ Include AI image
                  });

                  setShowVerdict(true);
                }}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg"
              >
                <Share2 size={18} />
                Share Verdict
              </button>
            </div>
          </div>
        );
      })()}

      {/* Secondary row */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {trial.hasVoted && (
            <span className="text-xs font-bold text-yellow-300 inline-flex items-center gap-1">
              <Trophy size={14} /> +50 coins earned
            </span>
          )}
        </div>
        
        {/* ENDED badge */}
        {ended && (
          <span className="text-xs font-black bg-slate-600 text-slate-200 px-3 py-1 rounded-full">
            ENDED
          </span>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for React.memo
  // Return true if props are equal (skip re-render), false to re-render
  
  // Always re-render if hasVoted changed
  if (prevProps.trial.hasVoted !== nextProps.trial.hasVoted) return false;
  
  // Always re-render if status changed
  if (prevProps.trial.status !== nextProps.trial.status) return false;
  
  // Always re-render if vote counts changed
  if (prevProps.trial.votesA !== nextProps.trial.votesA) return false;
  if (prevProps.trial.votesB !== nextProps.trial.votesB) return false;
  if (prevProps.trial.totalVotes !== nextProps.trial.totalVotes) return false;
  
  // Always re-render if timeLeft changed significantly
  if (Math.abs((prevProps.trial.timeLeft || 0) - (nextProps.trial.timeLeft || 0)) > 1) return false;
  
  // Check isNew prop
  if (prevProps.isNew !== nextProps.isNew) return false;
  
  // Otherwise, props are "equal" enough
  return true;
});

  StableTrialCardComponent = TrialCard;
  return StableTrialCardComponent;
}
